import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Drawer,
  Icon,
  Select,
  V2Badge,
  V2Button,
  V2ProgressBar,
  V2Skeleton,
  V2Tabs,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPatch, apiPost } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import {
  NO_PROJECT_LABEL,
  TASK_STATUS_OPTIONS,
  type AssignableMembersResponse,
  type ProjectTask,
  type TaskAssignee,
  type TaskChecklistItem,
} from '../../lib/types/projects.js';
import './project-tasks.css';

type StatusValue = (typeof TASK_STATUS_OPTIONS)[number]['value'];

const fa = (n: number) => n.toLocaleString('fa-IR');

function priorityTone(label: string): V2BadgeTone {
  if (label === 'فوری' || label === 'urgent') return 'danger';
  if (label === 'بالا' || label === 'high') return 'warn';
  if (label === 'متوسط' || label === 'medium') return 'info';
  return 'neutral';
}

function formatSpent(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${fa(m)} دقیقه`;
  return m === 0 ? `${fa(h)} ساعت` : `${fa(h)} ساعت و ${fa(m)} دقیقه`;
}

/**
 * Quick view of one task, opened from the board/list without leaving it.
 * Status and assignee changes go through the same endpoints as the full page;
 * the checklist can be ticked and extended here. Everything else (timesheet,
 * edit) stays on the full page, linked from the footer.
 */
export function TaskDrawer({
  slug,
  task,
  onClose,
  onMove,
  onAssigned,
}: {
  readonly slug: string;
  readonly task: ProjectTask;
  readonly onClose: () => void;
  readonly onMove: (task: ProjectTask, to: StatusValue) => void;
  readonly onAssigned: (ref: string, assignee: TaskAssignee | null) => void;
}) {
  const base = tenantBasePath(slug);
  const taskPath = `${base}/api/v1/projects/tasks/${encodeURIComponent(task.ref)}`;

  const [error, setError] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);

  const detail = useApiGet<ProjectTask>(taskPath, [task.ref]);
  const members = useApiGet<AssignableMembersResponse>(
    `${base}/api/v1/projects/assignable-members${
      task.projectRef ? `?projectRef=${encodeURIComponent(task.projectRef)}` : ''
    }`,
    [task.ref, task.projectRef],
  );

  // Local checklist so a tick shows at once and rolls back on failure.
  const [steps, setSteps] = useState<readonly TaskChecklistItem[] | null>(null);
  const [newStep, setNewStep] = useState('');
  const [addingStep, setAddingStep] = useState(false);
  const fetchedSteps = detail.status === 'ready' ? (detail.data.checklist ?? []) : null;
  useEffect(() => {
    if (fetchedSteps) setSteps(fetchedSteps);
  }, [fetchedSteps]);
  useEffect(() => {
    setSteps(null);
    setError(null);
    setNewStep('');
  }, [task.ref]);

  const currentStatus =
    TASK_STATUS_OPTIONS.find((o) => o.label === task.statusLabel)?.value ?? 'todo';

  async function onAssign(memberRef: string) {
    if (assigning) return;
    setAssigning(true);
    setError(null);
    try {
      const res = await apiPost<{ assignee: TaskAssignee | null }>(`${taskPath}/assign`, {
        assigneeMemberRef: memberRef === '' ? null : memberRef,
      });
      onAssigned(task.ref, res.assignee ?? null);
    } catch {
      setError('تغییر مسئول انجام نشد؛ دوباره تلاش کنید.');
    } finally {
      setAssigning(false);
    }
  }

  async function toggleStep(item: TaskChecklistItem) {
    const set = (isDone: boolean) =>
      setSteps((prev) =>
        prev ? prev.map((s) => (s.itemId === item.itemId ? { ...s, isDone } : s)) : prev,
      );
    set(!item.isDone);
    try {
      await apiPatch(`${taskPath}/checklist/${encodeURIComponent(item.itemId)}`, {
        isDone: !item.isDone,
      });
    } catch {
      set(item.isDone);
      setError('تغییر وضعیت مرحله انجام نشد.');
    }
  }

  async function addStep(event: FormEvent) {
    event.preventDefault();
    const title = newStep.trim();
    if (!title || addingStep) return;
    setAddingStep(true);
    setError(null);
    try {
      await apiPost(`${taskPath}/checklist`, { title });
      setNewStep('');
      detail.reload();
    } catch {
      setError('افزودن مرحله انجام نشد.');
    } finally {
      setAddingStep(false);
    }
  }

  const done = steps?.filter((s) => s.isDone).length ?? 0;
  const total = steps?.length ?? 0;
  const spent = task.totalSpentMinutes ?? 0;

  return (
    <Drawer
      open
      title={task.title}
      onClose={onClose}
      footer={
        <>
          <Link
            to={`${base}/projects/tasks/${encodeURIComponent(task.ref)}`}
            className="sk-btn sk-btn--secondary prj-drawer__full"
          >
            صفحهٔ کامل کار
          </Link>
          <V2Button variant="ghost" onClick={onClose}>
            بستن
          </V2Button>
        </>
      }
    >
      <div className="prj-drawer">
        <span className="prj-tasks__ref">{task.ref}</span>

        <section className="prj-drawer__section" aria-label="وضعیت">
          <h3 className="prj-drawer__label">وضعیت</h3>
          <V2Tabs
            items={TASK_STATUS_OPTIONS.map((o) => ({ id: o.value, label: o.label }))}
            activeId={currentStatus}
            onChange={(id) => onMove(task, id as StatusValue)}
          />
        </section>

        <section className="prj-drawer__section" aria-label="مسئول">
          <h3 className="prj-drawer__label">مسئول</h3>
          <Select
            aria-label="مسئول کار"
            value={task.assignee?.memberRef ?? ''}
            disabled={assigning || members.status === 'loading'}
            onChange={(e) => void onAssign(e.target.value)}
          >
            <option value="">بدون مسئول</option>
            {task.assignee &&
            members.status === 'ready' &&
            !members.data.members.some((m) => m.memberRef === task.assignee?.memberRef) ? (
              <option value={task.assignee.memberRef}>{task.assignee.displayLabel}</option>
            ) : null}
            {members.status === 'ready'
              ? members.data.members.map((m) => (
                  <option key={m.memberRef} value={m.memberRef}>
                    {m.displayLabel}
                    {m.isSelf ? ' (من)' : ''}
                  </option>
                ))
              : null}
          </Select>
          {members.status === 'error' ? (
            <span className="prj-drawer__hint">فهرست همکاران در دسترس نیست.</span>
          ) : null}
        </section>

        <dl className="prj-drawer__meta">
          <div>
            <dt>پروژه</dt>
            <dd>{task.projectRef ?? NO_PROJECT_LABEL}</dd>
          </div>
          <div>
            <dt>سررسید</dt>
            <dd>{task.dueDate ? displayJalaliDate(task.dueDate) : '—'}</dd>
          </div>
          <div>
            <dt>اولویت</dt>
            <dd>
              <V2Badge tone={priorityTone(task.priorityLabel)} plain>
                {task.priorityLabel}
              </V2Badge>
            </dd>
          </div>
          <div>
            <dt>زمان ثبت‌شده</dt>
            <dd>{spent > 0 ? formatSpent(spent) : '—'}</dd>
          </div>
          {task.relatedType ? (
            <div>
              <dt>مرتبط با</dt>
              <dd>
                {task.relatedTypeLabel ?? task.relatedType}
                {task.relatedRef ? `: ${task.relatedRef}` : ''}
              </dd>
            </div>
          ) : null}
        </dl>

        <section className="prj-drawer__section" aria-label="چک‌لیست">
          <h3 className="prj-drawer__label">
            چک‌لیست{steps && total > 0 ? ` · ${fa(done)} از ${fa(total)}` : ''}
          </h3>
          {steps && total > 0 ? <V2ProgressBar percent={(done / total) * 100} /> : null}
          {detail.status === 'loading' && steps === null ? (
            <div aria-busy="true" aria-live="polite" className="prj-drawer__steps">
              <V2Skeleton height={14} />
              <V2Skeleton width="70%" height={14} />
            </div>
          ) : null}
          {detail.status === 'error' && steps === null ? (
            <span className="prj-drawer__hint">چک‌لیست قابل نمایش نیست.</span>
          ) : null}
          {steps && total === 0 ? (
            <span className="prj-drawer__hint">هنوز مرحله‌ای ثبت نشده است.</span>
          ) : null}
          {steps && total > 0 ? (
            <ul className="prj-drawer__steps">
              {steps.map((s) => (
                <li key={s.itemId} className={s.isDone ? 'is-done' : undefined}>
                  <label>
                    <input
                      className="sk-check"
                      type="checkbox"
                      checked={s.isDone}
                      onChange={() => void toggleStep(s)}
                    />
                    <span>{s.title}</span>
                  </label>
                </li>
              ))}
            </ul>
          ) : null}
          {steps ? (
            <form className="prj-drawer__add" onSubmit={addStep}>
              <input
                className="sk-input"
                value={newStep}
                onChange={(e) => setNewStep(e.target.value)}
                placeholder="مرحلهٔ جدید…"
                aria-label="عنوان مرحلهٔ جدید"
              />
              <V2Button type="submit" size="sm" disabled={addingStep || newStep.trim() === ''}>
                <Icon name="plus" size="sm" />
                افزودن
              </V2Button>
            </form>
          ) : null}
        </section>

        {error ? (
          <Alert tone="danger" title="خطا">
            {error}
          </Alert>
        ) : null}
      </div>
    </Drawer>
  );
}
